/**
 * <ff-mega-menu> — the Frontier Fabrics category row and mega menu panels, rendered by
 * snippets/ff-mega-menu.liquid. Behaviour contract: docs/specs/phase4-header-mega-menu.md §6.
 *
 * - Mouse: hovering a top item opens its panel after 150 ms; moving to another top item while
 *   one is open switches instantly; the panel closes 300 ms after the pointer leaves. Side tabs
 *   switch on hover. Clicks follow links.
 * - Touch/pen: the first tap on a top item or side tab opens/switches, the second follows the link.
 * - Keyboard: each top item has a separate disclosure button (Enter/Space/↓ opens and moves focus
 *   in, Esc closes and returns focus, tabbing out closes); side tabs are a vertical tablist.
 *
 * Also keeps --header-height matched to the header's real height: this header style is taller
 * than Broadcast's Liquid estimate, and .theme__header is absolutely positioned over a backfill
 * sized by that variable.
 */
(() => {
  if (customElements.get('ff-mega-menu')) return;

  const OPEN_DELAY = 150;
  const CLOSE_DELAY = 300;
  const TAB_KEYS = {ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1};

  class FFMegaMenu extends HTMLElement {
    connectedCallback() {
      this.openItem = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.inputType = 'mouse';
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};

      this.querySelectorAll('[data-ff-mm-toggle]').forEach((toggle) => {
        toggle.hidden = false;
      });

      // Remember how the current interaction started: it decides whether a click opens or navigates
      document.addEventListener('pointerdown', (event) => (this.inputType = event.pointerType || 'mouse'), {...options, capture: true});
      document.addEventListener('keydown', () => (this.inputType = 'keyboard'), {...options, capture: true});

      document.addEventListener(
        'pointerdown',
        (event) => {
          if (this.openItem && !this.openItem.contains(event.target)) this.close();
        },
        options
      );

      this.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.addEventListener(
        'focusout',
        (event) => {
          if (this.inputType === 'keyboard' && this.openItem && !this.openItem.contains(event.relatedTarget)) this.close();
        },
        options
      );

      this.querySelectorAll('[data-ff-mm-item]').forEach((item) => this.bindItem(item, options));

      const header = this.closest('header-component');
      if (header) {
        this.watchHeaderState(header);
        this.syncHeaderHeight(header);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.stateObserver?.disconnect();
      this.heightObserver?.disconnect();
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
    }

    bindItem(item, options) {
      const panel = item.querySelector('[data-ff-mm-panel]');

      if (!panel) {
        // A plain link (Classes, Gift Cards): hovering it closes whichever panel is open
        item.addEventListener(
          'pointerenter',
          (event) => {
            if (event.pointerType === 'mouse') this.close();
          },
          options
        );
        return;
      }

      const top = item.querySelector('[data-ff-mm-top]');
      const toggle = item.querySelector('[data-ff-mm-toggle]');

      item.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          this.cancelClose();
          if (this.openItem === item) return;

          if (this.openItem) {
            this.open(item);
          } else {
            clearTimeout(this.openTimer);
            this.openTimer = setTimeout(() => this.open(item), OPEN_DELAY);
          }
        },
        options
      );

      item.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.openTimer);
          if (this.openItem === item) this.scheduleClose();
        },
        options
      );

      top.addEventListener(
        'click',
        (event) => {
          if (this.isTouch() && this.openItem !== item) {
            event.preventDefault();
            this.open(item);
          }
        },
        options
      );

      toggle.addEventListener(
        'click',
        () => {
          if (this.openItem === item) {
            this.close();
            return;
          }
          this.open(item);
          if (this.inputType === 'keyboard') this.focusFirst(item);
        },
        options
      );

      toggle.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          this.open(item);
          this.focusFirst(item);
        },
        options
      );

      panel.querySelectorAll('[data-ff-mm-tab]').forEach((tab) => {
        tab.addEventListener(
          'pointerenter',
          (event) => {
            if (event.pointerType === 'mouse') this.selectTab(tab);
          },
          options
        );

        // Focus selects a tab, and a tap focuses before it clicks — so record the state before the tap
        tab.addEventListener('pointerdown', () => (tab.dataset.wasSelected = tab.getAttribute('aria-selected')), options);
        tab.addEventListener('focus', () => this.selectTab(tab), options);

        tab.addEventListener(
          'click',
          (event) => {
            const wasSelected = tab.dataset.wasSelected === 'true';
            delete tab.dataset.wasSelected;

            if (!tab.hasAttribute('href') || (this.isTouch() && !wasSelected)) {
              event.preventDefault();
              this.selectTab(tab);
            }
          },
          options
        );
      });
    }

    onKeydown(event) {
      if (!this.openItem) return;

      if (event.key === 'Escape') {
        const toggle = this.openItem.querySelector('[data-ff-mm-toggle]');
        this.close();
        toggle?.focus();
        return;
      }

      const tab = event.target.closest('[data-ff-mm-tab]');
      if (!tab || !(event.key in TAB_KEYS)) return;

      event.preventDefault();
      const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[data-ff-mm-tab]')];
      const next = (tabs.indexOf(tab) + TAB_KEYS[event.key] + tabs.length) % tabs.length;
      tabs[next].focus();
    }

    open(item) {
      clearTimeout(this.openTimer);
      this.cancelClose();
      if (this.openItem === item) return;

      this.close();

      const panel = item.querySelector('[data-ff-mm-panel]');
      panel.hidden = false;
      item.classList.add('is-open');
      item.querySelector('[data-ff-mm-toggle]')?.setAttribute('aria-expanded', 'true');

      // Always open on the first side tab
      const firstTab = panel.querySelector('[data-ff-mm-tab]');
      if (firstTab) this.selectTab(firstTab);

      this.openItem = item;
    }

    close() {
      clearTimeout(this.openTimer);
      this.cancelClose();

      const item = this.openItem;
      if (!item) return;

      item.querySelector('[data-ff-mm-panel]').hidden = true;
      item.classList.remove('is-open');
      item.querySelector('[data-ff-mm-toggle]')?.setAttribute('aria-expanded', 'false');
      this.openItem = null;
    }

    scheduleClose() {
      this.cancelClose();
      this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
    }

    cancelClose() {
      clearTimeout(this.closeTimer);
    }

    selectTab(tab) {
      tab
        .closest('[role="tablist"]')
        .querySelectorAll('[data-ff-mm-tab]')
        .forEach((other) => {
          const selected = other === tab;
          other.setAttribute('aria-selected', String(selected));
          other.tabIndex = selected ? 0 : -1;

          const tabpanel = document.getElementById(other.getAttribute('aria-controls'));
          if (tabpanel) tabpanel.hidden = !selected;
        });
    }

    focusFirst(item) {
      const panel = item.querySelector('[data-ff-mm-panel]');
      const selectedTab = panel.querySelector('[data-ff-mm-tab][aria-selected="true"]');
      const firstVisibleLink = [...panel.querySelectorAll('a[href]')].find((link) => link.offsetParent !== null);
      (selectedTab || firstVisibleLink)?.focus();
    }

    isTouch() {
      return this.inputType === 'touch' || this.inputType === 'pen';
    }

    // Close on sticky or mobile-drawer state changes of Broadcast's <header-component>
    watchHeaderState(header) {
      const state = () => `${header.classList.contains('js__header__stuck')}|${header.classList.contains('js__show__mobile')}`;
      let lastState = state();

      this.stateObserver = new MutationObserver(() => {
        const currentState = state();
        if (currentState === lastState) return;
        lastState = currentState;
        this.close();
      });
      this.stateObserver.observe(header, {attributes: true, attributeFilter: ['class']});
    }

    syncHeaderHeight(header) {
      const bar = header.querySelector('[data-header-height]');
      if (!bar || header.hasAttribute('data-header-transparent') || !('ResizeObserver' in window)) return;

      this.heightObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--header-height', `${bar.offsetHeight}px`);
      });
      this.heightObserver.observe(bar);
    }
  }

  customElements.define('ff-mega-menu', FFMegaMenu);
})();
